import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  GitCompareArrows, CalendarRange, LayoutGrid, Table2, Download,
  FileSpreadsheet, FileDown, Trophy, TrendingDown, ArrowRight,
} from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { useTenant } from '@/context/TenantContext';
import { usePost } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { EmptyState } from '@/components/common/EmptyState';
import { CardSkeleton, GridSkeleton } from '@/components/common/Loading';
import { TrendBadge } from '@/components/common/TrendBadge';
import { ComparisonBars } from '@/components/charts/ComparisonBars';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { downloadPost } from '@/lib/download';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

const GRAN = [
  { key: 'week', label: 'Semaine' },
  { key: 'month', label: 'Mois' },
  { key: 'year', label: 'Année' },
];
const GRAN_HINT = {
  week: 'Évolution semaine par semaine',
  month: 'Évolution mois par mois',
  year: 'Évolution année par année',
};

const METRICS = {
  bs_reel: { label: 'BS validés', fmt: (v) => fmtInt(v), primary: true },
  bs_rue: { label: 'BS signés', fmt: (v) => fmtInt(v) },
  taux_transfo: { label: 'Taux de transformation', fmt: (v) => fmtPct(v) },
  don_moyen: { label: 'Don moyen', fmt: (v) => fmtEur(v, 2) },
  montant: { label: 'Montant collecté', fmt: (v) => fmtEur(v, 0) },
  productivite: { label: 'Productivité', fmt: (v) => `${fmtNum(v, 2)} BS/h` },
  heures: { label: 'Heures réalisées', fmt: (v) => fmtInt(v) },
  age_moyen: { label: 'Âge moyen', fmt: (v) => `${fmtNum(v, 1)} ans` },
};

// Executive summary cards (current vs previous period)
const SUMMARY_KEYS = ['bs_reel', 'montant', 'taux_transfo', 'don_moyen', 'age_moyen'];
const CHART_COLOR = 'var(--tenant-accent-600)';

export default function Comparaison() {
  const { body } = useFilters();
  const { association } = useTenant();
  const [gran, setGran] = useState('week');
  const [view, setView] = useState('executive');
  const [metric, setMetric] = useState('bs_reel');
  const [exporting, setExporting] = useState(null);

  const reqBody = useMemo(() => ({ ...body, granularity: gran }), [body, gran]);
  const { data, isLoading, isError } = usePost('/analytics/comparison-table', reqBody);

  const rows = data?.rows || [];
  const dataRows = rows.filter((r) => r.has_data);
  const summary = data?.summary;

  const chartData = useMemo(
    () => rows.map((r) => ({ label: r.label, value: r[metric] })),
    [rows, metric]
  );

  // Pour la granularité SEMAINE, affiche la plage de dates exacte (lundi → dimanche)
  // sous chaque barre du graphe — « S27 · 2026 » devient situable dans l'année.
  const weekSubLabels = useMemo(() => {
    if (gran !== 'week') return undefined;
    const m = {};
    rows.forEach((r) => { if (r.range_label) m[r.label] = r.range_label; });
    return Object.keys(m).length ? m : undefined;
  }, [rows, gran]);

  const runExport = async (kind) => {
    setExporting(kind);
    try {
      const ext = kind === 'comparison-excel' ? 'xlsx' : 'csv';
      const fname = `SHARITI_${body.association}_comparaison_${gran}.${ext}`;
      await downloadPost(`/exports/${kind}`, reqBody, fname);
      toast.success('Export généré');
    } catch (e) {
      toast.error('Export impossible');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Comparaison"
        icon={GitCompareArrows}
        subtitle={`${GRAN_HINT[gran]} · ${association?.short_name || association?.name || ''}`}
      />

      {/* Control bar */}
      <div className="flex flex-col gap-3 rounded-2xl border bg-card p-3 card-shadow sm:flex-row sm:items-center sm:justify-between"
        data-testid="comparison-controls">
        <div className="flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-muted-foreground" />
          <span className="mr-1 text-xs font-medium text-muted-foreground">Granularité</span>
          <ToggleGroup type="single" value={gran} onValueChange={(v) => v && setGran(v)}
            className="rounded-xl border bg-muted/40 p-1" data-testid="comparison-granularity">
            {GRAN.map((g) => (
              <ToggleGroupItem key={g.key} value={g.key}
                className="h-8 rounded-lg px-3 text-xs data-[state=on]:text-white"
                style={gran === g.key ? { background: 'var(--tenant-accent-500)' } : {}}
                data-testid={`gran-${g.key}`}>
                {g.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <Tabs value={view} onValueChange={setView} className="w-full sm:w-auto">
          <TabsList className="grid w-full grid-cols-2 rounded-xl sm:w-[280px]" data-testid="comparison-view-toggle">
            <TabsTrigger value="executive" className="gap-1.5 rounded-lg" data-testid="view-executive">
              <LayoutGrid className="h-3.5 w-3.5" /> Executive
            </TabsTrigger>
            <TabsTrigger value="analytique" className="gap-1.5 rounded-lg" data-testid="view-analytique">
              <Table2 className="h-3.5 w-3.5" /> Analytique
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {isError && <EmptyState title="Erreur de chargement" body="Impossible de récupérer la comparaison pour le moment." />}

      {isLoading ? (
        <>
          <GridSkeleton count={4} />
          <CardSkeleton />
        </>
      ) : dataRows.length === 0 ? (
        <EmptyState title="Aucune donnée à comparer"
          body="Aucune donnée sur la période sélectionnée. Élargissez la période ou changez de granularité." />
      ) : (
        <Tabs value={view} className="space-y-6">
          {/* ---------------- EXECUTIVE ---------------- */}
          <TabsContent value="executive" className="mt-0 space-y-6" data-testid="executive-panel">
            {summary && (
              <>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Dernière période</span>
                  <span className="rounded-full bg-muted px-2.5 py-1 font-display text-xs font-semibold" data-testid="summary-current-label">
                    {summary.current?.label_full || summary.current?.label}
                  </span>
                  {summary.previous && (
                    <>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">comparée à</span>
                      <span className="rounded-full bg-muted px-2.5 py-1 font-display text-xs font-semibold" data-testid="summary-previous-label">
                        {summary.previous.label_full || summary.previous.label}
                      </span>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid="executive-kpis">
                  {SUMMARY_KEYS.map((k) => {
                    const m = METRICS[k];
                    const kpi = summary.kpis?.[k] || {};
                    return (
                      <div key={k} className="flex flex-col rounded-2xl border bg-card p-5 card-shadow" data-testid={`exec-kpi-${k}`}>
                        <div className="flex items-start justify-between gap-2">
                          <p className="min-h-[2rem] break-words text-xs uppercase leading-tight tracking-wide text-muted-foreground">{m.label}</p>
                          <span className="shrink-0">
                            <TrendBadge value={kpi.pct}
                              tooltip={summary.previous ? `${m.label} : comparé à « ${summary.previous.label_full || summary.previous.label} » (période précédente).` : undefined}
                              testid={`exec-kpi-${k}-trend`} />
                          </span>
                        </div>
                        <p className="mt-3 whitespace-nowrap font-display text-[1.7rem] font-semibold leading-none tabular text-foreground"
                          style={m.primary ? { color: 'var(--tenant-accent-600)' } : {}}>
                          {m.fmt(kpi.value)}
                        </p>
                        {kpi.previous !== null && kpi.previous !== undefined && (
                          <p className="mt-2 whitespace-nowrap text-xs text-muted-foreground">
                            préc. <span className="tabular font-medium text-foreground/70">{m.fmt(kpi.previous)}</span>
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Best / worst */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {data?.best && (
                <div className="flex items-center gap-3 rounded-2xl border bg-card p-4 card-shadow" data-testid="best-period">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50">
                    <Trophy className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Meilleure période</p>
                    <p className="font-display text-sm font-semibold">{data.best.label_full || data.best.label} · {fmtInt(data.best.bs_reel)} BS validés</p>
                  </div>
                </div>
              )}
              {data?.worst && (
                <div className="flex items-center gap-3 rounded-2xl border bg-card p-4 card-shadow" data-testid="worst-period">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50">
                    <TrendingDown className="h-5 w-5 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Période la plus basse</p>
                    <p className="font-display text-sm font-semibold">{data.worst.label_full || data.worst.label} · {fmtInt(data.worst.bs_reel)} BS validés</p>
                  </div>
                </div>
              )}
            </div>

            {/* Evolution chart */}
            <SectionCard
              title={`Évolution — ${METRICS[metric].label}`}
              subtitle={GRAN_HINT[gran]}
              action={
                <Select value={metric} onValueChange={setMetric}>
                  <SelectTrigger className="h-9 w-[190px] rounded-xl bg-card" data-testid="chart-metric-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(METRICS).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              }
              testid="evolution-chart-card">
              <ComparisonBars
                data={chartData}
                xKey="label"
                height={340}
                subLabels={weekSubLabels}
                formatTip={(v) => METRICS[metric].fmt(v)}
                series={[{ key: 'value', name: METRICS[metric].label, color: CHART_COLOR }]}
              />
            </SectionCard>
          </TabsContent>

          {/* ---------------- ANALYTIQUE ---------------- */}
          <TabsContent value="analytique" className="mt-0 space-y-6" data-testid="analytique-panel">
            <SectionCard
              title="Tableau d'évolution détaillé"
              subtitle={`${GRAN_HINT[gran]} — variation vs période précédente`}
              action={
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="gap-1.5 rounded-xl"
                    onClick={() => runExport('comparison-excel')} disabled={exporting === 'comparison-excel'}
                    data-testid="export-comparison-excel">
                    <FileSpreadsheet className="h-4 w-4" />
                    <span className="hidden sm:inline">{exporting === 'comparison-excel' ? '…' : 'Excel'}</span>
                  </Button>
                  <Button variant="outline" size="sm" className="gap-1.5 rounded-xl"
                    onClick={() => runExport('comparison-csv')} disabled={exporting === 'comparison-csv'}
                    data-testid="export-comparison-csv">
                    <FileDown className="h-4 w-4" />
                    <span className="hidden sm:inline">{exporting === 'comparison-csv' ? '…' : 'CSV'}</span>
                  </Button>
                </div>
              }
              bodyClassName="pt-0"
              testid="comparison-table-card">
              <div className="overflow-hidden rounded-xl border">
                <Table data-testid="comparison-table">
                  <TableHeader>
                    <TableRow className="bg-muted/60 hover:bg-muted/60">
                      <TableHead className="sticky left-0 z-10 bg-muted/60 font-semibold text-foreground">Période</TableHead>
                      <TableHead className="text-right">BS signés</TableHead>
                      <TableHead className="text-right">BS validés</TableHead>
                      <TableHead className="text-right">Δ</TableHead>
                      <TableHead className="text-right">Transfo</TableHead>
                      <TableHead className="text-right">Don moyen</TableHead>
                      <TableHead className="text-right">Âge moy.</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead className="text-right">Δ Mnt</TableHead>
                      <TableHead className="text-right">Prod.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.period} className={r.has_data ? '' : 'opacity-45'} data-testid={`comparison-row-${r.period}`}>
                        <TableCell className="sticky left-0 z-10 bg-card font-medium">
                          <div className="flex flex-col leading-tight">
                            <span>
                              {r.label}
                              {!r.has_data && <span className="ml-1.5 text-[10px] text-muted-foreground">(vide)</span>}
                            </span>
                            {gran === 'week' && r.range_label && (
                              <span className="text-[10px] font-normal text-muted-foreground">{r.range_label}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular">{fmtInt(r.bs_rue)}</TableCell>
                        <TableCell className="text-right tabular font-semibold">{fmtInt(r.bs_reel)}</TableCell>
                        <TableCell className="text-right">
                          <TrendBadge value={r.deltas?.bs_reel?.pct} className="justify-end" />
                        </TableCell>
                        <TableCell className="text-right tabular">{fmtPct(r.taux_transfo)}</TableCell>
                        <TableCell className="text-right tabular">{fmtEur(r.don_moyen, 2)}</TableCell>
                        <TableCell className="text-right tabular">{fmtNum(r.age_moyen, 1)} ans</TableCell>
                        <TableCell className="text-right tabular">{fmtEur(r.montant, 0)}</TableCell>
                        <TableCell className="text-right">
                          <TrendBadge value={r.deltas?.montant?.pct} className="justify-end" />
                        </TableCell>
                        <TableCell className="text-right tabular">{fmtNum(r.productivite, 2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              {data?.totals && (
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl accent-bg-soft px-4 py-3 text-sm"
                  data-testid="comparison-totals">
                  <span className="font-display font-semibold">Total période</span>
                  <span>BS signés : <span className="tabular font-medium">{fmtInt(data.totals.bs_rue)}</span></span>
                  <span>BS validés : <span className="tabular font-medium">{fmtInt(data.totals.bs_reel)}</span></span>
                  <span>Montant : <span className="tabular font-medium">{fmtEur(data.totals.montant, 0)}</span></span>
                  <span>Transfo moy. : <span className="tabular font-medium">{fmtPct(data.totals.taux_transfo)}</span></span>
                  <span>Âge moyen : <span className="tabular font-medium">{fmtNum(data.totals.age_moyen, 1)} ans</span></span>
                </div>
              )}
            </SectionCard>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
